<template>
  <div id="big-boxes-hit-points" class="big-box">
    <div class="row tight">

      <div class="col-1 text-center">
        <div>
          <div class="text-center" v-if="canEdit">
            <a @click="increase('current')" class="plus-minus"><i class="fa fa-plus-circle"></i></a><br>
            <a @click="decrease('current')" class="plus-minus"><i class="fa fa-minus-circle"></i></a>
          </div>
        </div>
      </div>

      <div class="col text-center">
        <div class="text-center">
          <strong class="primary">{{ totalHitPoints }}</strong>
          <nobr style="white-space: nowrap;">
            <span class="hp-slash" v-if="tempHitPoints">/</span>
            <span class="hp-temp" v-if="tempHitPoints">{{ tempHitPoints }}</span>
          </nobr>
        </div>
      </div>

      <div class="col-shrink text-center">
        <span v-if="health === 'dead'" class="health dead">💀</span>
        <i v-if="health === 'unconcious'" class="fa fa-bed health unconcious" aria-hidden="true"></i>
        <i v-if="health === 'bloodied'" class="fa fa-heartbeat health bloodied" aria-hidden="true"></i>
        <i v-if="health === 'hurt'" class="fa fa-heartbeat health hurt" aria-hidden="true"></i>
        <i v-if="health === 'full'" class="fa fa-heartbeat health full" aria-hidden="true"></i>
      </div>

      <hit-points></hit-points>

      <div class="col-1 text-center">
        <div>
          <div class="text-center" v-bind:class="{ 'hp-temp-enabled': tempHitPoints }" v-if="canEdit">
            <a @click="increase('temp')" class="plus-minus"><i class="fa fa-plus-circle"></i></a><br>
            <a @click="decrease('temp')" class="plus-minus"><i class="fa fa-minus-circle"></i></a>
          </div>
        </div>
      </div>
    </div>

    <div class="life-bar" :class="[isHurt ? 'pink' : 'red']">
      <span :style="{ width: lifeBar + '%' }"></span>
    </div>

    <div class="topline">
      <div class="row">
        <div class="col-5 text-center">
          <label>
            <span class="d-none d-md-inline">Hit Points</span>
            <span class="d-inline d-md-none">HPs</span>
          </label>
        </div>
        <div class="col text-right" v-if="['5th Edition', '5e24', 'Solarian Conflict'].includes(ruleset)">
          <label @click="toggleState = !toggleState" style="padding-right: .3em; cursor: pointer;">
            <span class="d-none d-md-inline">Hit Dice | Death Saves</span>
            <span class="d-inline d-md-none">HD | Death Saves</span>
          </label>
          <toggle :id="'hit-points-' + this._uid" v-model="toggleState"></toggle>
        </div>
        <div class="col text-right" v-if="['PFRPG2', 'Embers RPG'].includes(ruleset) && dying">
          <label @click="toggleState = !toggleState" style="padding-right: .3em; cursor: pointer;">
            <span class="d-inline">Dying | Wounds</span>
          </label>
          <toggle :id="'hit-points-' + this._uid" v-model="toggleState"></toggle>
        </div>
      </div>
    </div>

    <div v-if="toggleState" class="little-box">
      <div class="row" v-if="['5th Edition', '5e24', 'Solarian Conflict'].includes(ruleset)">
        <div class="col">
          <hit-dice></hit-dice>
        </div>
        <div class="col-5">
          <death-saves></death-saves>
        </div>
      </div>
      <div class="row" v-if="['PFRPG2', 'Embers RPG'].includes(ruleset)">
        <div class="col">
          <dying></dying>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from "vuex";
import DeathSaves from "./_DeathSaves.vue";
import Dying from "./_Dying.vue";
import HitDice from "./_HitDice.vue";
import HitPoints from "./_HitPoints.vue";
import UnderInput from "../shared/_UnderInput.vue";
import Toggle from "../shared/_Toggle.vue";

export default {
  name: "big-boxes-health",
  components: {
    DeathSaves,
    Dying,
    HitDice,
    HitPoints,
    UnderInput,
    Toggle,
  },
  data() {
    return {
      toggleState: false,
    };
  },
  watch: {
    isUnconcious: "checkToggle",
  },
  computed: {
    ...mapGetters({ canEdit: "characters/canEdit" }),
    ...mapState({
      ruleset: (state) => state.characters.current.ruleset,
      hitPoints: (state) => state.characters.current.defense.hitPoints,
      deathSaves: (state) => state.characters.current.defense.deathSaves,
      dying: (state) => state.characters.current.defense.dying,
      dead: (state) => state.characters.current.dead,
      attributes: (state) => state.characters.current.attributes,
    }),
    totalHitPoints() {
      let hp = 0;
      if (Number.isFinite(this.hitPoints.current)) hp += this.hitPoints.current;
      if (Number.isFinite(this.hitPoints.temp)) hp += this.hitPoints.temp;
      return hp;
    },

    tempHitPoints() {
      let hp = 0;
      if (Number.isFinite(this.hitPoints.temp)) hp = this.hitPoints.temp;
      return hp;
    },

    health() {
      let constitution = this.attributes.find((x) => x.name === "constitution");
      let payload = {
        dead: this.dead,
        hitPoints: {
          current: this.hitPoints.current,
          maximum: this.hitPoints.maximum,
        },
        constitution: constitution.score,
      };

      if (this.deathSaves) {
        payload.deathSaves = this.deathSaves.failures.length;
      }

      if (this.dying) {
        payload.dying = this.dying.current;
      }

      return this.$rulesets[this.ruleset].health(payload);

      // return this.$rpgv.d20.health(payload);
    },

    isHurt() {
      return this.health === "hurt";
    },

    isUnconcious() {
      return this.health === "unconcious";
    },

    lifeBar() {
      let health = Math.round(
        (this.hitPoints.current / this.hitPoints.maximum) * 100
      );
      health = health > 100 ? 100 : health;
      return health;
    },
  },
  methods: {
    ...mapActions("characters", ["autoSave"]),
    checkToggle() {
      if (
        ["PFRPG2", "5th Edition", "5e24", "Solarian Conflict", "Embers RPG"].includes(
          this.ruleset
        )
      ) {
        if (this.isUnconcious) this.toggleState = true;
      }
    },
    decrease(hp) {
      if (!Number.isFinite(this.hitPoints[hp])) this.hitPoints[hp] = 0;

      if (
        ["PFRPG2", "Embers RPG"].includes(this.ruleset) &&
        this.hitPoints[hp] == 1
      ) {
        this.dying.current = 1 + this.dying.wounds;
      }

      // reduce temp first
      if (Number.isFinite(this.hitPoints.temp) && this.hitPoints.temp > 0) {
        this.hitPoints.temp -= 1;
      } else {
        this.hitPoints[hp] -= 1;
      }

      if (
        ["PFRPG2", "5th Edition", "5e24", "Solarian Conflict", "Embers RPG"].includes(
          this.ruleset
        ) &&
        this.hitPoints[hp] < 0
      ) {
        this.hitPoints[hp] = 0;
      }
      this.autoSave();
    },
    increase(hp) {
      if (!Number.isFinite(this.hitPoints[hp])) this.hitPoints[hp] = 0;
      if (this.hitPoints[hp] < this.hitPoints.maximum) {
        this.hitPoints[hp] += 1;
      }
      if (
        ["PFRPG2", "Embers RPG"].includes(this.ruleset) &&
        this.hitPoints[hp] > 0
      ) {
        this.dying.current = 0;
        if (this.dying.wounds < 3) this.dying.wounds++;
      }
      this.autoSave();
    },
  },
};
</script>
<style lang="scss" scoped>
@import "../../../../assets/_global";

.hp-slash {
  color: $gray-600;
  font-size: 1.5em;
  line-height: 1em;
}
.hp-temp {
  color: $gray-600;
  font-size: 1em;
}
.plus-minus {
  font-size: 1.55em;
  line-height: 0.875em;
}
.mainline {
  border-bottom: 0;
}
.topline {
  font-weight: bold;
}
.health {
  margin-top: 0.15em;
  font-size: 2.5em;
  &.dead {
    margin-top: 0;
    font-size: 2.25em;
    line-height: 1.3;
  }
  &.unconcious {
    color: $gray-400;
  }
  &.bloodied {
    color: red;
  }
  &.hurt {
    color: pink;
  }
  &.full {
    color: $gray-400;
  }
}

.life-bar {
  height: 3px;
  position: relative;
  background: $gray-600;
}
.life-bar > span {
  display: block;
  height: 100%;
  background-color: $gray-600;
  position: relative;
  overflow: hidden;
}
.pink {
  background-color: pink;
}
.red {
  background-color: red;
}
.little-box {
  margin-bottom: 0.5em;
}
</style>
